import Request from "../Request.js";
import store from "@/store";
import {
  damageDetectionAvailable,
  damageDetectionActivated,
  detectionActivatedAt,
} from "@/utils/damage-detection.js";

const FFSOLAR_SERVER_URL = window.runtimeConfig?.FFSOLAR_SERVER_URL;
// A damage without a gallery photo cannot be shown anywhere, so it never reaches the viewer.
const withImage = (items) => (items || []).filter((ann) => ann.image_id != null);

// The damage projection pipeline writes its annotations into Mongo as soon as it
// runs, which is long before anyone asked to see them. They stay out of the
// viewer entirely — model markers, sidebar, gallery, PDF — until the company's
// subscription has the inspections add-on AND the project confirmed the modal
// once, which stamps damage_detection_mode_activated_at on its config.
// The add-on gate is checked here too, not just on the tool button: a company
// losing it should stop seeing the damages it already revealed. Both the
// add-ons and the project config are fetched before the model loads, which is
// what makes this safe to read synchronously.
// Filtering here rather than at each call site is what keeps the viewer and the
// gallery showing the same list: both go through this function.
const optedInToAiDamages = () =>
  damageDetectionAvailable() && damageDetectionActivated();
const withoutHiddenAiDamages = (items) =>
  optedInToAiDamages()
    ? items
    : (items || []).filter((ann) => !ann.automatically_evaluated);

// Damages are numbered by their position in this array, so this is the one place
// the order is decided — the backend's own sort is plain creation order.
const creationTime = (ann) => (ann.dateAdded ? new Date(ann.dateAdded).getTime() : 0);

// Manual annotations drawn before the damage detection was switched on keep the
// front of the list, in the order they were drawn: that is the inspector's own
// work from back when there was nothing to interleave it with, and renumbering
// it around AI damages that appeared later would move findings people already
// refer to by number. Everything from that moment on — later manual annotations
// and every AI damage, whenever the pipeline happened to import it — is one
// chronological run after them.
//
// With no activation stamp there are no AI damages to show either, so nothing
// is held back and the whole list is plain creation order, as before.
const precedesDetection = (ann, activatedAt) =>
  activatedAt !== null &&
  !ann.automatically_evaluated &&
  creationTime(ann) < activatedAt;

export const sortAnnotations = (items, activatedAt = detectionActivatedAt()) =>
  [...(items || [])].sort((a, b) => {
    const group = (ann) => (precedesDetection(ann, activatedAt) ? 0 : 1);
    return (
      group(a) - group(b) ||
      creationTime(a) - creationTime(b) ||
      String(a.id).localeCompare(String(b.id))
    );
  });

const annotations = {
  async getAnnotations(projectId) {
    const response = await Request.get(`${FFSOLAR_SERVER_URL}/projects/${projectId}/annotations`);
    const visible = (items) => sortAnnotations(withoutHiddenAiDamages(withImage(items)));
    if (Array.isArray(response.data?.results)) {
      response.data.results = visible(response.data.results);
    } else if (Array.isArray(response.data)) {
      response.data = visible(response.data);
    }
    return response;
  },
  analyzeAnnotation(projectId, imageBase64, language) {
    return Request.post(`${FFSOLAR_SERVER_URL}/annotations/prediction`, {
      project_id: Number(projectId),
      imageBase64: imageBase64,
      language: language || 'de',
    });
  },
  deleteAnnotation(annotationId) {
    return Request.delete(`${FFSOLAR_SERVER_URL}/annotations/${annotationId}`);
  },
  saveAnnotation(projectId, imagePk, data) {
    return Request.post(`${FFSOLAR_SERVER_URL}/annotations`, {
      project_id: Number(projectId),
      image_id: imagePk,
      automatically_evaluated: false,
      damage_type: data.damageType,
      severity_level: data.severityLevel,
      description: data.description,
      recommended_action: data.recommendedAction,
      predicted_type: data.predictedType,
      predicted_severity_level: data.predictedSeverityLevel,
      predicted_description: data.predictedDescription,
      predicted_recommended_actions: data.predictedRecommendedActions,
      x: data.imgX,
      y: data.imgY,
      width: data.imgWidth,
      height: data.imgHeight,
      norm_x: data.normX,
      norm_y: data.normY,
      image_index: data.imageIndex,
      frame_image: data.cropImageUrl,
    });
  },
  updateAnnotation(annotationId, projectId, imagePk, data) {
    return Request.put(`${FFSOLAR_SERVER_URL}/annotations`, {
      id: annotationId,
      project_id: Number(projectId),
      image_id: imagePk,
      damage_type: data.damageType,
      severity_level: data.severityLevel,
      description: data.description,
      recommended_action: data.recommendedAction,
      predicted_type: data.predictedType,
      predicted_severity_level: data.predictedSeverityLevel,
      predicted_description: data.predictedDescription,
      predicted_recommended_actions: data.predictedRecommendedActions,
      x: data.imgX,
      y: data.imgY,
      width: data.imgWidth,
      height: data.imgHeight,
      norm_x: data.normX,
      norm_y: data.normY,
      image_index: data.imageIndex,
      frame_image: data.cropImageUrl,
    });
  },
};

export default annotations;

export const uploadCropImage = (projectId, imageBase64) =>
  Request.post(`${window.runtimeConfig.FFSOLAR_SERVER_URL}/annotations/crop-image`, {
    project_id: projectId,
    image_base64: imageBase64,
  });
